import { CalendarCheck, Gauge, Play, Target, Timer, TrendingUp, Users } from "lucide-react";
import { Suspense, lazy } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { defineFeature } from "../types";

// React.lazy so the settings page costs the entry bundle nothing but its icon.
const TimeSettingsPage = lazy(() => import("./settings"));

/**
 * Time — timers, the weekly timesheet, approvals, budgets and profitability
 * (PRD §8 Item 10, US-10.1 … US-10.8).
 *
 * Budgets and profitability live here rather than in features of their own
 * because they are the same domain seen from the money side: the same entries,
 * the same cache entries, the same `financials:view` mask. A separate feature
 * would have had to reach into this one's internals, which frontend-conventions
 * §1.5 forbids.
 *
 * `/time?entry=<id>` is fixed by ENTITY_ROUTES — search results and
 * notifications link there, so the week page opens that exact entry.
 */
export default defineFeature({
  key: "time",
  title: "Time",
  entities: ["time_entry"],
  routes: [
    { path: "time", lazy: () => import("./page") },
    { path: "time/team", lazy: () => import("./team") },
    { path: "time/profitability", lazy: () => import("./profitability") },
    { path: "budgets", lazy: () => import("./budgets") },
  ],
  nav: [
    {
      label: "Time",
      to: "/time",
      icon: Timer,
      group: "delivery",
      order: 30,
      permission: "time:track",
      description: "Timers, timesheets and utilisation",
    },
    {
      label: "Budgets",
      to: "/budgets",
      icon: Target,
      group: "delivery",
      order: 35,
      permission: ["financials:view", "projects:read"],
      description: "How much of each project has been spent",
    },
  ],
  quickCreate: [
    {
      key: "time",
      label: "Time entry",
      icon: Timer,
      to: "/time?new=1",
      permission: "time:track",
      description: "Log hours against a task",
    },
  ],
  commands: [
    {
      key: "time.timer",
      label: "Start a timer",
      icon: Play,
      group: "actions",
      permission: "time:track",
      keywords: ["stopwatch", "track"],
      // A DOM event rather than an import: the timer widget is lazy, and the
      // registry must not pull it into the entry chunk to open its dialog.
      run: ({ close }) => {
        close();
        window.dispatchEvent(new CustomEvent("l2a:timer-start", { detail: { taskId: null } }));
      },
    },
    { key: "time.week", label: "My week", icon: CalendarCheck, to: "/time", group: "navigate", permission: "time:track", keywords: ["timesheet", "hours"] },
    { key: "time.team", label: "Team time and approvals", icon: Users, to: "/time/team", group: "navigate", permission: "time:manage", keywords: ["approve", "timesheets", "utilisation"] },
    {
      key: "time.approvals",
      label: "Approve timesheets",
      icon: CalendarCheck,
      to: "/time/team?tab=approvals",
      group: "actions",
      permission: "time:manage",
      keywords: ["submitted", "queue"],
    },
    {
      key: "time.profitability",
      label: "Client profitability",
      icon: TrendingUp,
      to: "/time/profitability",
      group: "navigate",
      permission: ["reports:view", "financials:view"],
      keywords: ["margin", "effective rate", "worst clients"],
    },
    { key: "time.budgets", label: "Budget burn", icon: Gauge, to: "/budgets", group: "navigate", permission: "financials:view", keywords: ["burn", "overspend"] },
  ],
  settingsSections: [
    {
      key: "time-tracking",
      label: "Time tracking",
      path: "time",
      group: "Delivery",
      order: 130,
      icon: Timer,
      description: "Rounding, the first day of the week, approvals and reminders",
      permission: "time:track",
      element: (
        <Suspense fallback={<Skeleton className="h-72 w-full" rounded="card" />}>
          <TimeSettingsPage />
        </Suspense>
      ),
    },
  ],
});
